"use client";

import { RegisteredComponent } from "@builder.io/sdk-react";
import dynamic from "next/dynamic";

import { AemModel } from "@jti/user-profile";

import { IMAGE_FILE_TYPES } from "@/libs/builder/consts";
import { getBuilderModelName } from "@/libs/builder/tools/getBuilderModelName";

const UserProfile = dynamic(
  async () => (await import("./UserProfile")).UserProfile,
  {
    ssr: false,
  }
);

export const userProfileInputs = [
  {
    name: "payOnDelivery",
    type: "object",
    required: true,
    defaultValue: {
      paymentMethod:
        "You have chosen payment on delivery, please make sure to follow up with delivery and have the exact amount when you receive the product.",
    },
    subFields: [
      {
        name: "paymentMethod",
        type: "richText",
        required: true,
      },
    ],
  },
  {
    name: "ageVerificationMethodImages",
    type: "reference",
    model: getBuilderModelName("age-verification-method-images"),
  },
  {
    name: "zeroValueOrder",
    type: "object",
    required: true,
    defaultValue: {
      userNotification: "This order is free of charge. No payment required.",
    },
    subFields: [
      {
        name: "userNotification",
        type: "richText",
        required: true,
      },
    ],
  },
  {
    name: "headerBanner",
    type: "object",
    defaultValue: {
      hideGreetingMessage: false,
    },
    subFields: [
      {
        name: "hideGreetingMessage",
        type: "boolean",
        required: true,
      },
    ],
  },
  {
    name: "failedPaymentFlow",
    type: "object",
    defaultValue: {
      title: "Your order is pending payment",
      message: "Please proceed to payment to complete your order.",
      submitButtonLabel: "Proceed to payment",
    },
    subFields: [
      {
        name: "title",
        type: "string",
        required: true,
      },
      {
        name: "message",
        type: "richText",
        required: true,
      },
      {
        name: "submitButtonLabel",
        type: "string",
        required: true,
      },
    ],
  },
  {
    name: "communicationPreferences",
    type: "object",
    subFields: [
      {
        name: "isLanguagePreferenceHidden",
        type: "boolean",
        required: true,
      },
      {
        name: "purposesGroupingType",
        type: "string",
        required: true,
        enum: ["category", "section"],
      },
      {
        name: "successUpdateMessage",
        type: "richText",
        required: true,
      },
      {
        name: "oneTrustErrorMessage",
        type: "richText",
        required: true,
      },
      {
        name: "oneTrustErrorLinkText",
        type: "string",
        required: true,
      },
      {
        name: "oneTrustErrorRedirectUrl",
        type: "url",
        required: true,
      },
      {
        name: "emailFieldDescription",
        type: "string",
        required: true,
      },
      {
        name: "phoneFieldDescription",
        type: "string",
        required: true,
      },
      {
        name: "surveyFieldDescription",
        type: "string",
        required: true,
      },
      {
        name: "postFieldDescription",
        type: "string",
        required: true,
      },
      {
        name: "gdprFieldDescription",
        type: "string",
        required: true,
      },
      {
        name: "purposesGroupingType",
        type: "string",
        required: true,
        enum: ["category", "section"],
      },
      {
        name: "isEmailChannelDisabled",
        type: "boolean",
        required: true,
        defaultValue: false,
      },
      {
        name: "isPhoneChannelDisabled",
        type: "boolean",
        required: true,
        defaultValue: false,
      },
      {
        name: "isPostChannelDisabled",
        type: "boolean",
        required: true,
        defaultValue: false,
      },
      {
        name: "consentOptionsListType",
        type: "string",
        enum: ["default", "extended"],
      },
    ],
    defaultValue: { purposesGroupingType: "category" },
  },
  {
    name: "genderInformation",
    type: "object",
    subFields: [
      {
        name: "isGenderEnabled",
        type: "boolean",
        required: true,
      },
    ],
  },
  {
    name: "deviceManagement",
    type: "object",
    required: true,
    defaultValue: {
      isLastDeviceRemovalRestricted: false,
      deviceCodeLength: "short",
      successfulDeviceRegistrationCTA:
        "You have successfully registered your device.",
    },
    subFields: [
      {
        name: "isLastDeviceRemovalRestricted",
        type: "boolean",
        required: true,
      },
      {
        name: "deviceCodeLength",
        type: "string",
        required: true,
        enum: ["short", "long", "gen4"],
      },
      {
        name: "successfulDeviceRegistrationCTA",
        type: "richText",
        required: true,
      },
    ],
  },
  {
    name: "sharableProfile",
    type: "object",
    subFields: [
      {
        name: "isSharableProfileEnabled",
        type: "boolean",
        required: true,
      },
      {
        name: "isSharableProfileLoyaltyEnabled",
        type: "boolean",
        required: true,
      },
    ],
  },
  {
    name: "orderList",
    type: "object",
    subFields: [
      {
        name: "isOfflineOrderEnabled",
        type: "boolean",
        required: true,
      },
    ],
  },
  {
    name: "personalInformation",
    type: "object",
    subFields: [
      {
        name: "isDobChangeRestricted",
        type: "boolean",
        required: true,
      },
      {
        name: "dobChangeRestrictedMessage",
        type: "string",
        required: true,
      },
    ],
  },
  {
    name: "passPersonalDataUpdate",
    type: "object",
    required: true,
    defaultValue: {
      isPassPersonalDataUpdate: false,
      title: "Age Verification",
      logoSrc: "",
      logoAlt: "logo alt",
      description:
        "By continuing, you agree to our Terms of Service and Privacy Policy.",
      continueButtonLabel: "Continue",
      tryAgainButtonLabel: "Try again",
      backButtonLabel: "Back",
      legalNotice:
        "Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqu",
      desktopImageSrc: "",
      tabletImageSrc: "",
      mobileImageSrc: "",
      verificationTitle: "Verifying your phone number",
      alt: "alt",
      verificationSteps:
        "To access this site, please verify your identity and phhone number using PASS AV",
      failedProviderVerificationError:
        "Failed to verify your phone number. Please try again.",
      waitingMessage:
        "We are verifying your phone number. This may take a few seconds.",
      helperText:
        "Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqu",
      verificationErrorHelperText: "verification error helper text",
      missingDataFromAvError: "missing data from av error",
    },
    subFields: [
      {
        name: "isPassPersonalDataUpdate",
        type: "boolean",
        required: true,
      },
      {
        name: "logoSrc",
        type: "file",
        required: false,
        allowedFileTypes: IMAGE_FILE_TYPES,
      },
      {
        name: "logoAlt",
        type: "string",
        required: false,
      },
      {
        name: "title",
        type: "string",
        required: true,
      },
      {
        name: "description",
        type: "richText",
        required: false,
      },
      {
        name: "continueButtonLabel",
        type: "string",
        required: false,
      },
      {
        name: "tryAgainButtonLabel",
        type: "string",
        required: false,
      },
      {
        name: "backButtonLabel",
        type: "string",
        required: false,
      },
      {
        name: "legalNotice",
        type: "richText",
        required: false,
      },
      {
        name: "desktopImageSrc",
        type: "file",
        required: false,
        allowedFileTypes: IMAGE_FILE_TYPES,
      },
      {
        name: "tabletImageSrc",
        type: "file",
        required: false,
        allowedFileTypes: IMAGE_FILE_TYPES,
      },
      {
        name: "mobileImageSrc",
        type: "file",
        required: false,
        allowedFileTypes: IMAGE_FILE_TYPES,
      },
      {
        name: "alt",
        type: "string",
        required: false,
      },
      {
        name: "verificationTitle",
        type: "string",
        required: false,
      },
      {
        name: "verificationSteps",
        type: "richText",
        required: false,
      },
      {
        name: "failedProviderVerificationError",
        type: "string",
        required: false,
      },
      {
        name: "waitingMessage",
        type: "richText",
      },
      {
        name: "helperText",
        type: "string",
        required: false,
      },
      {
        name: "verificationErrorHelperText",
        type: "string",
        required: false,
      },
      {
        name: "missingDataFromAvError",
        type: "string",
        required: false,
      },
    ],
  },
  {
    name: "subscriptionConfiguration",
    type: "object",
    defaultValue: {
      title: "Subscription config title",
    },
    subFields: [
      {
        name: "bannerBackgroundImage",
        type: "file",
        required: false,
        allowedFileTypes: IMAGE_FILE_TYPES,
      },
      {
        name: "title",
        type: "string",
        required: false,
      },
      {
        name: "priceDisclaimerEnabled",
        type: "boolean",
        required: false,
      },
      {
        name: "subscribeNowUrl",
        type: "string",
        required: false,
      },
    ],
  },
  {
    name: "referredByFriendConfiguration",
    type: "object",
    defaultValue: {
      discountImageDesktop: "",
      discountImageTablet: "",
      discountImageMobile: "",
      successImageDesktop: "",
      successImageMobile: "",
      successHeading: "",
      successLabel: "",
      successBtnLabel: "",
      successLink: "",
      heading: "",
      label: "",
      list: [],
      legalText: "",
      discountHeading: "",
      discountLabel: "",
      discountSubText: "",
      validationRequiredMessage: "",
      verifyErrorMessage: "",
      inputLabel: "",
      inputPlaceholder: "",
      inputHelperText: "",
      btnText: "",
      isInEditor: false,
      isHidden: false,
    },
    subFields: [
      {
        name: "discountImageDesktop",
        type: "file",
        allowedFileTypes: IMAGE_FILE_TYPES,
      },
      {
        name: "discountImageTablet",
        type: "file",
        allowedFileTypes: IMAGE_FILE_TYPES,
      },
      {
        name: "discountImageMobile",
        type: "file",
        allowedFileTypes: IMAGE_FILE_TYPES,
      },
      {
        name: "successImageDesktop",
        type: "file",
        allowedFileTypes: IMAGE_FILE_TYPES,
      },
      {
        name: "successImageMobile",
        type: "file",
        allowedFileTypes: IMAGE_FILE_TYPES,
      },
      {
        name: "successHeading",
        type: "string",
      },
      {
        name: "successLabel",
        type: "string",
      },
      {
        name: "successBtnLabel",
        type: "string",
      },
      {
        name: "successLink",
        type: "string",
      },
      {
        name: "heading",
        type: "string",
      },
      {
        name: "label",
        type: "string",
      },
      {
        name: "list",
        type: "list",
        defaultValue: [],
      },
      {
        name: "legalText",
        type: "richText",
      },
      {
        name: "discountHeading",
        type: "string",
      },
      {
        name: "discountLabel",
        type: "string",
      },
      {
        name: "discountSubText",
        type: "string",
      },
      {
        name: "validationRequiredMessage",
        type: "string",
      },
      {
        name: "verifyErrorMessage",
        type: "string",
      },
      {
        name: "inputLabel",
        type: "string",
      },
      {
        name: "inputPlaceholder",
        type: "string",
      },
      {
        name: "inputHelperText",
        type: "string",
      },
      {
        name: "btnText",
        type: "string",
      },
      {
        name: "isInEditor",
        type: "boolean",
        defaultValue: false,
      },
      {
        name: "isHidden",
        type: "boolean",
        defaultValue: false,
      },
    ],
  },
  {
    name: "deleteAccount",
    type: "object",
    defaultValue: {
      header: "Delete Account",
      checkboxContent:
        "I understand that deleting my account will remove all my data and preferences.",
      content:
        "Are you sure you want to delete your account? This action cannot be undone.",
      successCallbackContent:
        "Your account has been successfully deleted. We are sorry to see you go.",
      errorCallbackContent:
        "There was an error deleting your account. Please try again later.",
      deletionType: "optionsDeletion",
      deactivationRadioButtonText: "Deactivate my account",
      cleanDeletionRadioButtonText: "Delete my account permanently",
      successfulDisablingAccountMessage:
        "Your account has been successfully deactivated.",
      disableFailedErrorMessage:
        "There was an error deactivating your account. Please try again later.",
    } satisfies AemModel["deleteAccount"],
    subFields: [
      {
        name: "header",
        type: "string",
        required: true,
      },
      {
        name: "checkboxContent",
        type: "richText",
        required: true,
      },
      {
        name: "content",
        type: "richText",
        required: true,
      },
      {
        name: "successCallbackContent",
        type: "richText",
        required: true,
      },
      {
        name: "errorCallbackContent",
        type: "richText",
        required: true,
      },
      {
        name: "deletionType",
        type: "string",
        enum: ["optionsDeletion", "cleanDeletion"],
        required: true,
      },
      {
        name: "deactivationRadioButtonText",
        type: "string",
        required: true,
      },
      {
        name: "cleanDeletionRadioButtonText",
        type: "string",
        required: true,
      },
      {
        name: "successfulDisablingAccountMessage",
        type: "string",
        required: true,
      },
      {
        name: "disableFailedErrorMessage",
        type: "string",
        required: true,
      },
    ],
  },
] as const satisfies Inputs;

export const UserProfileComponent: RegisteredComponent = {
  name: "UserProfile",
  component: UserProfile,
  inputs: userProfileInputs,
};
